<template>
  <div :class="controlClasses" class="mds-form-control mds-suggestion-select">
    <label :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :style="innerStyles" class="inner">
      <div class="control-row">
        <div v-if="selectSource.isLoading" class="loader-dash" />
        <div class="mds-form-control-icon" v-if="hasIconSlot">
          <!-- @slot Слот иконки -->
          <slot name="icon" />
        </div>

        <input
          ref="searchInput"
          type="text"
          autocomplete="off"
          :disabled="isDisabled"
          :id="uid"
          :placeholder="placeholder"
          @input="onSearchValueInput"
          v-model="searchValue"
          @focusin="onSearchInputFocusIn"
          @focusout="onSearchInputFocusOut"
          @blur="onSearchInputBlur"
        />

        <button
          v-if="showClear"
          type="button"
          tabindex="-1"
          class="clear"
          @click="onClear"
        >
          <VIcon material="cancel" />
        </button>
      </div>

      <div class="messages-row">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="empty-data" v-if="showEmptyDataText">
          {{ emptyDataText }}
        </div>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>
    </div>

    <VPopup
      ref="selectPopup"
      class="mds-suggestion-popup-wrapper"
      @keydown="keyDownListeners"
      @update:visible="setFocusOut"
      :offset="1"
      :target="'#' + uid"
      :visible="isFocused"
      align="left"
      inherit-width
      no-pointer
      position="bottom"
    >
      <div class="mds-suggestion-popup">
        <div
          v-show="Utils.isNotEmptyArray(options) || hasChosenValue"
          class="mds-suggestion-select-options"
        >
          <div
            v-for="(option, key) of options"
            :key="key"
            class="mds-suggestion-select-options-item"
            :class="getOptionClasses(option, key)"
            @click.stop="setChosenValue(option)"
          >
            <template v-if="hasOptionSlot">
              <slot name="option" v-bind:option="option" />
            </template>
            <template v-else>
              <div v-html="option.getHighlightedText(chosenValue?.toString() ?? '')"></div>
              <ul v-if="Array.isArray(option.description) === true" class="description">
                <li v-for="line of option.description" :key="line">{{ line }}</li>
              </ul>
              <div
                v-else-if="Utils.isNullOrUndefined(option.description) === false"
                class="description"
              >
                {{ option.description }}
              </div>
            </template>
          </div>
        </div>

        <div
          v-show="options.length === 0"
          class="mds-suggestion-select-options-empty"
        >
          <template v-if="hasEmptyOptionsSlot">
            <!-- @slot Слот вспомогательного текста -->
            <slot name="empty-options" />
          </template>
          <template v-else>
            Ничего не найдено
          </template>
        </div>
      </div>
    </VPopup>
  </div>
</template>

<style lang="scss" src="./VSuggestionSelectV2.scss" />

<script lang="ts">
import debounce     from "lodash.debounce";
import Core         from "@/core/Core";
import Utils        from '@/core/utils/Utils';
import VBaseControl from "@/core/services/VBaseControl";
import SelectLoader from "@/lib/components/VSelectV2/SelectLoader";

import VIcon  from "@/lib/components/VIcon/VIcon.vue";
import VPopup from "@/lib/components/VPopup/VPopup.vue";

import SuggestionSelectOption from "./SuggestionSelectOption";

export default defineComponent({
  name: "VSuggestionSelectV2",

  components: { VPopup, VIcon },

  extends: VBaseControl,

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /** v-model */
    modelValue: {
      type:    [Array, Object],
      default: null,
    },
    /**
     * Откуда берем данные, варианты
     * - loader
     * - функция сервиса под 1 параметр поиска, напр PartnersService.searchSeller
     * - либо сырые данные
     */
    data: {
      type:    [Function, Object, Array],
      default: null,
    },
    /**
     * Класс сущности для свободного ввода. Задан — наверх уходит инстанс
     * с заполненным именем (см. статику fromSuggestion модели), не задан —
     * плоский { id, name }. Существующие опции приходят с бека сущностями
     * и от пропа не зависят.
     */
    type: {
      type:    Function as PropType<any>,
      default: null,
    },
    /** Имя свойства объекта-идентификатора */
    optionIdentity: {
      type:    String,
      default: "id",
    },
    /** Скрыть кнопку очистки (по умолчанию показана) */
    hideClearBtn: {
      type:    Boolean,
      default: false,
    },
    /**
     * Текст под контролом, когда источник загрузился и опций нет
     * («Нет неоплаченных отгрузок»). Не показывается, пока данные
     * не запрашивались (пустой родитель, прелоад не сработал).
     */
    emptyDataText: {
      type:    String,
      default: null,
    },
  },

  emits: ['update:modelValue', 'blur', 'focus'],

  data() {
    return {
      Utils,
      isFocused:    false,
      selectSource: SelectLoader.from(this.data),
      /**
       * Дебаунс поиска. Создаётся на экземпляр в created: объявленный в
       * methods он создавался бы один раз на компонент, и его таймер был бы
       * общим для всех контролов на странице — ввод в один отменял бы поиск
       * в соседнем.
       */
      searchDebounced: null,
      /** Значение, которое пользователь вводит или выбирает */
      chosenValue: new SuggestionSelectOption(null),
      /** Значение, предложенное как автодополнение */
      supposedValue: null,
      /** Словарь отменённых вхождений — не реагировать автодополнением */
      cancelledSupposedValues: [],
      /**
       * Выбор через стрелки. Хранится индексом, а не самой опцией: options —
       * вычисляемое свойство, оно пересоздаёт SuggestionSelectOption на
       * каждый пересчёт, и ссылка между рендерами не переживает.
       */
      hoveredIndex: null,
    };
  },

  created() {
    this.validateType();

    this.searchDebounced = debounce(() => {
      this.search(this.searchValue);
    }, 300);

    /**
     * При создании компонента нужно миновать сеттер chosenValue, тк сеттер
     * триггерит эмит наверх, а нам нужно этого избежать.
     */
    this.chosenValue = new SuggestionSelectOption(this.modelValue);

    // Предзагрузка при монтировании, если источник так настроен.
    // После загрузки — автовыбор: единственный вариант предвыбирается
    // всегда, кроме поисковых источников (туда прелоад не доезжает).
    if (this.selectSource.isPreloadOnMount === true) {
      this.selectSource.loadOnMount().then(() => {
        this.autoSelectIfValueEmpty();
      });
    }
    // Статичный источник: опции уже есть, грузить нечего — только автовыбор.
    else if (this.selectSource.isStatic === true) {
      this.autoSelectIfValueEmpty();
    }
  },

  beforeUnmount() {
    // Отложенный поиск после размонтирования контрола не нужен.
    this.searchDebounced.cancel();
  },

  watch: {
    /** Отслеживаем изменения модели снаружи */
    modelValue(newVal, oldVal) {
      if (Core.utils.isEqual(newVal, oldVal) === true) return;
      // Если снаружи пришло то же, что уже выбрано — не пересоздаём chosenValue,
      // иначе сеттер searchValue заэмитит update:modelValue и зациклит watcher.
      if (this.isSameAsChosen(newVal) === true) return;
      this.searchValue = newVal;
    },

    /**
     * Смена родителя зависимого источника. Перезагружаем список и, если
     * текущее значение больше не консистентно новому списку, сбрасываем его
     * и подставляем новое.
     *
     * Консистентное значение не трогаем: подстановка умолчаний не должна
     * затирать уже сделанный выбор.
     */
    async sourceDependencyKey(newKey, oldKey) {
      if (newKey === oldKey) {
        // родитель тот же — список перезагружать не от чего
      }
      else {
        const isReloaded = await this.selectSource.reloadOnDependantChange();

        if (isReloaded === false) {
          // источник не перезагружается по смене родителя
        }
        else if (this.isValueEmpty === false && this.isValueConsistentWithOptions() === true) {
          if (this.selectSource.isApplied(this.chosenValue.asModelValue) === true) {
            /** Значение стоит с прошлой автоподстановки, а не введено руками —
             *  пересчитываем его под нового родителя. Список при этом мог и не
             *  измениться: дефолт может зависеть от родителя напрямую. */
            this.applyAutoValue();
          }
          else {
            // введено или выбрано пользователем — не трогаем
          }
        }
        else {
          /** Значение не из этого списка — сбрасываем, дальше подставляем
           *  заново. Пустое onClear переживёт молча. */
          this.onClear();

          this.applyAutoValue();
        }
      }
    },
  },

  computed: {
    /**
     * Контрол выключен пропом disabled ИЛИ источник недоступен
     * (зависимый источник с незаполненным родителем).
     */
    isDisabled(): boolean {
      if (this.disabled === true) {
        return true;
      } else {
        return this.selectSource.isDependentValueNotEmpty === false;
      }
    },

    /** «Загрузили и пусто» — показываем emptyDataText. */
    showEmptyDataText(): boolean {
      if (this.emptyDataText === null) {
        return false;
      } else {
        return this.selectSource.isLoadedAndEmpty;
      }
    },

    /**
     * Ключ зависимостей источника — для watch'а перезагрузки при смене
     * родителя. null, если источника нет или он независимый.
     */
    sourceDependencyKey(): string | null {
      return this.selectSource.dependencyKey;
    },

    /**
     * Опция под курсором стрелок. Достаётся по индексу из актуального
     * списка — так подсветка переживает пересчёт options.
     */
    hoveredOption() {
      if (this.hoveredIndex === null) {
        return null;
      } else {
        return this.options[this.hoveredIndex] ?? null;
      }
    },

    /** Какое-то значение было предложено как автодополнение */
    isOptionSupposed(): boolean {
      return this.supposedValue !== null && this.supposedValue !== undefined;
    },

    hasChosenValue(): boolean {
      /** trim() — чтобы при нащёлкивании пробелов не показывать пустую строку */
      return Utils.string.isString(this.chosenValue.name)
        && this.chosenValue.name.trim() !== "";
    },

    /** Значение поисковой строки */
    searchValue: {
      set(value) {
        // При возобновлении ввода нужно обнулять подсветку стрелок.
        this.hoveredIndex = null;
        const _value = new SuggestionSelectOption(value);
        this.setChosenValue(_value);
      },
      get() {
        // Если есть выбранная стрелочками опция — показываем её в поисковой строке.
        if (this.hoveredOption !== null) {
          return this.hoveredOption.toString();
        }
        return this.chosenValue.toString();
      },
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) result["width"] = `calc(100% - ${this.labelWidth})`;
      return result;
    },

    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    /** Поле лейбла пустое — значение пустое */
    hasValue(): boolean {
      return this.chosenValue.name !== null
        && this.chosenValue.name !== "";
    },

    isValueEmpty(): boolean {
      return this.hasValue === false;
    },

    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.isFocused === true)  classes.push("focused");
      if (this.isValueEmpty)        classes.push("empty");
      return classes;
    },

    showClear(): boolean {
      return this.hideClearBtn === false
        && this.disabled === false
        && this.isValueEmpty === false;
    },

    hasIconSlot(): boolean {
      return this.$slots.icon !== undefined;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },

    hasEmptyOptionsSlot(): boolean {
      return this.$slots["empty-options"] !== undefined;
    },

    /**
     * Загруженные опции источника, обёрнутые в SuggestionSelectOption, чтобы
     * они умели приводиться к строке и размечать подсветку вхождения.
     * Считается от опций источника, а не копится снимком при загрузке —
     * иначе опции из pinOptions(Promise), разрешившиеся позже, в список
     * не попадают.
     */
    loadedSuggestionSelectOptions() {
      return this.selectSource.options.map(
        (option) => new SuggestionSelectOption(option)
      );
    },

    /** Класс из пропа type умеет строить сущность из свободного ввода. */
    hasFromSuggestion(): boolean {
      if (this.type === null) {
        return false;
      } else {
        return typeof this.type.fromSuggestion === 'function';
      }
    },

    /** Первой опцией в списке стоит текущий свободный ввод. */
    hasInputOption(): boolean {
      if (this.hasChosenValue === true && this.isOptionSupposed === false) {
        return true;
      } else {
        return false;
      }
    },

    /** Варианты для показа в дропдауне */
    options() {
      if (this.hasInputOption === true) {
        // Только уникальные.
        return this.getUniqueObjectsById(
          [this.chosenValue].concat(this.loadedSuggestionSelectOptions)
        );
      }

      return this.loadedSuggestionSelectOptions;
    },

    hasOptionSlot(): boolean {
      return this.$slots.option !== undefined;
    },
  },

  methods: {
    /**
     * Класс передали, а строить сущность он не умеет — это ошибка разработчика,
     * молчать нельзя: наверх уйдёт { id, name } вместо ожидаемой сущности.
     */
    validateType() {
      if (this.type === null) {
        return;
      } else if (this.hasFromSuggestion === false) {
        console.error(`[VSuggestionSelectV2] у ${this.type.name} нет статики fromSuggestion(name). Свободный ввод уйдёт наверх как { id, name }.`);
      }
    },

    /**
     * Значение для v-model. Существующая опция — сущность с бека как есть.
     * Свободный ввод — инстанс класса из пропа type, а если тип не задан или
     * не умеет строить сущность — плоский { id, name }, как было до пропа.
     */
    toModelValue(suggestionSelectOption) {
      if (suggestionSelectOption.isExisting === true) {
        return suggestionSelectOption.asModelValue;
      } else if (this.hasFromSuggestion === false) {
        return suggestionSelectOption.asModelValue;
      } else {
        return this.type.fromSuggestion(suggestionSelectOption.name);
      }
    },

    /** Устанавливаем выбранное значение и эмитим v-model */
    setChosenValue(suggestionSelectOption) {
      // Сравниваем по сути значение входящей опции с текущим выбором.
      // asModelValue приводит опцию к той же форме, что уходит в v-model
      // ({ id, name } или сущность), — сравнение с chosenValue идёт единообразно.
      const incomingValue = suggestionSelectOption ? suggestionSelectOption.asModelValue : null;
      if (this.isSameAsChosen(incomingValue) === true) return;
      if (suggestionSelectOption) this.chosenValue = suggestionSelectOption;

      // Единая точка установки, поэтому метку автоподстановки снимаем здесь.
      // Автоподстановка ставит её обратно сразу после вызова.
      this.selectSource.markApplied(null);

      this.$emit("update:modelValue", this.toModelValue(this.chosenValue));
    },

    /**
     * Совпадает ли входящее значение с уже выбранным. Входящее — { id, name }
     * (свободный ввод) или сущность с бека; и то и другое имеет id и name.
     * chosenValue тоже держит id и name. Сравниваем по сути: по id, если оба
     * существующие; для свободного ввода (id пустой у обоих) — по name.
     */
    isSameAsChosen(value): boolean {
      const chosenName = this.chosenValue.name;

      // Оба пустые.
      if (
        (value === null || value === undefined)
        && (chosenName === null || chosenName === undefined || chosenName === "")
      ) {
        return true;
      }

      // Один пустой, другой нет.
      if (value === null || value === undefined) return false;
      if (chosenName === null || chosenName === undefined || chosenName === "") return false;

      const isValueExisting  = value.id !== undefined && value.id !== null;
      const isChosenExisting = this.chosenValue.id !== undefined && this.chosenValue.id !== null;

      // неявное создание и реальная опция с тем же именем — разные значения,
      // иначе выбор опции после автоподстановки не проходит
      if (isValueExisting !== isChosenExisting) {
        return false;
      }

      // Оба с непустым id — сравниваем по id (сущности с бека).
      if (isValueExisting === true && isChosenExisting === true) {
        return value.id === this.chosenValue.id;
      }

      // Иначе (свободный ввод) — сравниваем по name.
      return value.name === chosenName;
    },

    /**
     * Выбранное значение есть в текущем списке опций.
     *
     * Свободный ввод в списке не значится по определению — считаем его
     * консистентным всегда, иначе смена родителя стирала бы то, что
     * пользователь набрал руками.
     */
    isValueConsistentWithOptions(): boolean {
      if (this.isValueEmpty === true) return true;
      if (this.chosenValue.isExisting === false) return true;

      const availableIds = new Set(
        this.loadedSuggestionSelectOptions.map((option) => option.id),
      );

      return availableIds.has(this.chosenValue.id);
    },

    /**
     * Подстановка значения, когда его ещё нет: единственная опция, иначе
     * дефолт источника. Зеркало VSelectV2.
     */
    autoSelectIfValueEmpty(): void {
      if (this.isValueEmpty === false) {
        return;
      }

      this.applyAutoValue();
    },

    /**
     * Единственная опция, иначе дефолт источника.
     * Заполнено значение или нет, не проверяет: решает вызывающий.
     */
    applyAutoValue(): void {
      if (this.loadedSuggestionSelectOptions.length === 1) {
        this.autoSelectSingleOption();
      }
      else {
        this.autoSelectDefaultValue();
      }
    },

    /**
     * В списке ровно одна опция — подставляем её: выбирать не из чего.
     *
     * Заполнено значение или нет, не проверяет: решает вызывающий.
     */
    autoSelectSingleOption(): void {
      if (this.loadedSuggestionSelectOptions.length !== 1) return;

      const single = this.loadedSuggestionSelectOptions[0];
      this.setChosenValue(single);
      this.selectSource.markApplied(single.asModelValue);
    },

    /**
     * Пытаемся инициализироваться дефолтным значением источника.
     * null из defaultValue — осознанное отсутствие значения, его и ставим;
     * undefined означает, что дефолт не сработал.
     */
    autoSelectDefaultValue(): void {
      if (this.selectSource.hasDefault === false) { return; }

      const value = this.selectSource.defaultValue;

      if (value === undefined) { return; }
      else {
        this.setChosenValue(new SuggestionSelectOption(value));
        this.selectSource.markApplied(value);
      }
    },

    onSearchValueInput() {
      this.searchDebounced();
    },

    onSearchInputFocusIn() {
      this.setFocusIn();
    },

    onSearchInputFocusOut() {
      this.setFocusOut();
    },

    async onSearchInputBlur() {
      await this.validate();
    },

    setFocusOut() {
      this.isFocused = false;
      window.removeEventListener("keydown", this.keyDownListeners);
      const searchInput = this.$refs.searchInput;
      if (searchInput && typeof searchInput.blur === "function") searchInput.blur();
      // Наверх отдаём ту же форму, что и в v-model.
      this.$emit("blur", this.toModelValue(this.chosenValue));
    },

    setFocusIn() {
      this.validation.markTouched();

      // Если на момент фокуса есть какое-то значение — ищем по нему.
      if (this.searchValue) this.search(this.searchValue, false);

      /**
       * Предзагрузка при фокусе, если источник так настроен и опции ещё
       * не загружены. Уже загруженные не перезапрашиваем — смена родителя
       * обновляет их сама.
       */
      this.selectSource.loadOnFocus().then((isLoaded) => {
        if (isLoaded === true) {
          this.autoSelectIfValueEmpty();
        }
        else {
          // загрузки не было — подставлять нечего
        }
      });

      setTimeout(() => {
        this.isFocused = true;
        this.$emit("focus");
        window.addEventListener("keydown", this.keyDownListeners);
      });
    },

    /** Поиск по вхождению */
    async search(query, needToAutoFinish = true) {
      if (this.selectSource.isLoadOnInputPattern === false) {
        // Источник не грузит по вводу — список уже загружен.
        if (needToAutoFinish === true) {
          this.autoFinishSearchValue(query);
        }
        return;
      }

      if (query === "") {
        await this.selectSource.load();
        return;
      }

      await this.selectSource.load(query);

      if (needToAutoFinish === true) {
        this.autoFinishSearchValue(query);
      }
    },

    /**
     * Классы опции. hovered — по позиции: опции пересоздаются вычисляемым
     * свойством, и сравнение по ссылке не совпадало бы никогда.
     * selected — по сути: chosenValue пересоздаётся на каждый ввод.
     */
    getOptionClasses(option, index) {
      const result: Record<string, boolean> = {};

      if (index === this.hoveredIndex) result.hovered = true;
      if (this.isSameAsChosen(option.asModelValue) === true) result.selected = true;

      return result;
    },

    autoFinishSearchValue(searchTerm) {
      /**
       * Для автодополнения нужен массив вариантов и длина <= 4 — 4 оставшихся
       * варианта хватит, чтобы пользователь "угадал" нужный.
       */
      if (
        Utils.isNotEmptyArray(this.options) === false
        || this.options.length > 4
        || searchTerm === ""
      ) {
        return;
      }

      /**
       * autoFinishSearchValue срабатывает всегда, в т.ч. при стирании
       * автоподставленного слова. Без проверки ниже был бы бесконечный
       * цикл подставлений.
       */
      if (this.isOptionSupposed === true) {
        this.supposedValue = null;
        /**
         * При побуквенном стирании автоподстановка не должна срабатывать
         * после каждой стёртой буквы. Поэтому ведём словарь исключений.
         */
        this.cancelledSupposedValues = this.generateSubwords(searchTerm);
        return;
      }

      // Текущее вхождение в чёрном списке — отменяем автоподставление.
      if (this.cancelledSupposedValues.includes(searchTerm) === true) return;

      const lowerCaseSearchTerm = searchTerm.toLowerCase();

      // Берём максимально подходящий вариант.
      this.supposedValue = this.findMostCompatibleItem(lowerCaseSearchTerm);

      if (this.supposedValue === null || this.supposedValue === undefined) return;

      this.searchValue = this.supposedValue.toString();

      this.$nextTick(() => {
        // Выделяем ту часть фразы, которую подставили.
        this.$refs.searchInput.setSelectionRange(
          searchTerm.length,
          this.supposedValue.toString().split("").length,
        );
        /** Автодополнили опцию — красим её как hovered. Ищем позицию в
         *  актуальном списке: сама опция к этому моменту уже могла быть
         *  пересоздана пересчётом options. */
        this.hoveredIndex = this.findOptionIndex(this.supposedValue);
      });
    },

    /**
     * Позиция опции в текущем списке. Сравниваем по сути, а не по ссылке:
     * options пересоздаёт обёртки на каждый пересчёт.
     */
    findOptionIndex(suggestionSelectOption): number | null {
      if (this.$core.utils.isNullOrUndefined(suggestionSelectOption)) {
        return null;
      }

      const index = this.options.findIndex(
        (option) => this.isSameOption(option, suggestionSelectOption),
      );

      if (index === -1) {
        return null;
      } else {
        return index;
      }
    },

    /**
     * Одна и та же опция: существующие сверяются по id, свободный ввод — по
     * имени. По id одному нельзя: у свободного ввода он undefined, и любые
     * две такие опции считались бы одинаковыми.
     */
    isSameOption(one, another): boolean {
      if (one.isExisting === true && another.isExisting === true) {
        return one.id === another.id;
      } else {
        return one.name === another.name;
      }
    },

    /** Поиск максимально подходящего варианта */
    findMostCompatibleItem(searchTerm) {
      /**
       * Главный критерий — совпадение начала строки. Цель: подставить опцию
       * в контрол так, чтобы курсор не "прыгнул" вправо.
       */
      return this.loadedSuggestionSelectOptions.find((option) =>
        option.toString().toLowerCase().startsWith(searchTerm)
      );
    },

    keyDownListeners(event) {
      /**
       * Перенёс как есть из VSelect — назначение этого кода непонятно,
       * но вероятно для чего-то было.
       */
      if (event.target.tagName !== "INPUT" || event.key === "Enter") {
        event.preventDefault();
      }

      switch (event.key) {
        /** Обрабатываем стрелку вниз */
        case "ArrowDown": {
          /** Если hovered элемента у нас нет */
          if (this.hoveredIndex === null) {
            if (this.hasInputOption === true) {
              // Первая опция — введённое значение, её всегда игнорируем.
              this.hoveredIndex = 1;
            } else {
              // Опции свободного ввода в списке нет — идём с самой первой.
              this.hoveredIndex = 0;
            }
            return;
          }

          /** Последний в списке — зацикливаем на первый */
          if (this.hoveredIndex === this.options.length - 1) {
            this.hoveredIndex = 0;
          } else {
            this.hoveredIndex = this.hoveredIndex + 1;
          }
          break;
        }
        case "ArrowUp": {
          // При ArrowUp каретка убегает в начало строки — предотвращаем.
          event.preventDefault();

          if (this.hoveredIndex === null) {
            this.hoveredIndex = this.options.length - 1;
            return;
          }

          /** Первый в списке — зацикливаем на последний */
          if (this.hoveredIndex === 0) {
            this.hoveredIndex = this.options.length - 1;
          } else {
            this.hoveredIndex = this.hoveredIndex - 1;
          }
          break;
        }
        case "Enter":
          // Выбор стрелками + Enter — установить как выбранное.
          if (this.hoveredOption !== null) {
            this.setChosenValue(this.hoveredOption);
          } else {
            this.setChosenValue(this.supposedValue);
          }
          this.setFocusOut();
          break;
        case "Escape":
          this.setFocusOut();
          break;
      }
    },

    /** "Фонарь" → ["Фонарь", "Фонар", "Фона", "Фон", "Фо", "Ф"] */
    generateSubwords(word) {
      const subwords = [];
      for (let i = word.length; i > 0; i--) {
        subwords.push(word.slice(0, i));
      }
      return subwords;
    },

    onClear() {
      // Сбрасываем состояние явно: без этого сброс приходил обратным прогоном
      // через watch modelValue и сеттер searchValue, давая второй эмит
      // update:modelValue, а чёрный список автоподстановок переживал очистку.
      this.chosenValue             = new SuggestionSelectOption(null);
      this.supposedValue           = null;
      this.hoveredIndex            = null;
      this.cancelledSupposedValues = [];

      // setChosenValue здесь не вызывается, метку снимаем явно.
      this.selectSource.markApplied(null);

      this.$emit("update:modelValue", null);
      this.$emit("blur", null);
    },

    getUniqueObjectsById(arr) {
      const uniqueIds = new Set();
      return arr.filter((item) => {
        const identity = item[this.optionIdentity];
        if (uniqueIds.has(identity) === false) {
          uniqueIds.add(identity);
          return true;
        }
        return false;
      });
    },

    /**
     * Запускает валидацию текущего значения через Validation.
     * Гейт touched+dirty — контрол сам решает, когда показывать ошибки.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      await this.validation.validate(this.modelValue);
    },
  },
});
</script>
